import { FLOODING } from '@/consts';
import { HOW_MUCH_REPAIR_COST_NAME } from '@/features/v2/hh/components/InputHowMuchRepairCost';
import { HOW_MUCH_INSURER_PAID_NAME } from '@/features/v2/hh/components/InputHowMuchTheInsurerPaid';
import {
  ANY_INVOICES,
  ANY_INVOICES_NAME,
} from '@/features/v2/hh/components/RadioGroupAnyInvoices';
import {
  DRYING_METHOD,
  DRYING_METHOD_NAME,
} from '@/features/v2/hh/components/RadioGroupDryingMethod';
import {
  SETTLEMENT_FORM,
  SETTLEMENT_FORM_NAME,
} from '@/features/v2/hh/components/RadioGroupDryingSettlementForm';
import {
  INSURER_DECISION,
  INSURER_DECISION_NAME,
} from '@/features/v2/hh/components/RadioGroupIssuerDecision';
import {
  SETTLEMENT,
  SETTLEMENT_NAME,
} from '@/features/v2/hh/components/RadioGroupSettlement';
import {
  SETTLEMENT_WAS_SUFFICIENT,
  SETTLEMENT_WAS_SUFFICIENT_NAME,
} from '@/features/v2/hh/components/RadioGroupSettlementWasSufficient';
import {
  POLICY_TYPE,
  POLICY_TYPE_NAME,
} from '@/features/v2/hh/components/SelectPolicyType';
import {
  hhDocumentsSteps,
  hhDocumentsStepsWithBoth,
  hhDocumentsStepsWithBothAndDryingReport,
  hhDocumentsStepsWithDryingReport,
  hhDocumentsStepsWithInvoices,
  hhDocumentsStepsWithInvoicesAndDryingReport,
  hhDocumentsStepsWithPolicy,
  hhDocumentsStepsWithPolicyAndDryingReport,
} from '@/features/v2/hh/const/documentsSteps';
import { Step1HH, Step2HH, Step3HH } from '@/features/v2/hh/steps';
import {
  DAMAGE_LOCATION_ADDRESS,
  DAMAGE_LOCATION_BUILDING_NUMBER,
  DAMAGE_LOCATION_CITY,
  DAMAGE_LOCATION_ZIP_CODE,
} from '@/features/v2/hh/surveys/SurveyTwo';
import { DeadEnds, ExtendedSteps } from '@/features/v2/lib/wizard';
import { EnumLike } from '@/types';

import { AdditionalInfo } from '../lib/additionalInfo';
import {
  WHAT_WAS_REPAIRED_NAME,
  WHAT_WAS_REPAIRED_OPTIONS,
} from './components/CheckboxGroupWhatWasRepaired';
import {
  BANK_CESSION,
  BANK_CESSION_NAME,
} from './components/RadioGroupBankCession';
import {
  DAMAGE_REPAIRED,
  DAMAGE_REPAIRED_NAME,
} from './components/RadioGroupDamageRepaired';
import { FIX_METHOD, FIX_METHOD_NAME } from './components/RadioGroupFixMethod';
import {
  IS_COVERED_BY_MORTGAGE,
  IS_COVERED_BY_MORTGAGE_NAME,
} from './components/RadioGroupIsCoveredByMortgage';
import {
  OWNERSHIP_TYPE,
  OWNERSHIP_TYPE_NAME,
} from './components/RadioGroupOwnershipType';
import { PROPERTY_OWNERSHIP_STATUS_NAME } from './components/RadioGroupPropertyOwnershipStatus';
import { VAT_PAYER_NAME } from './components/RadioGroupVatPayer';
import {
  WILL_APPEAL,
  WILL_APPEAL_NAME,
} from './components/RadioGroupWillAppeal';
import { BANK_NAME } from './components/SelectBank';
import { ISSUE_TYPE_NAME } from './components/SelectIssueType';
import { i18n } from '@lingui/core';
import { isNil } from 'lodash';

const isEconomicFixMethod = (val: Record<string, unknown>) =>
  val[FIX_METHOD_NAME] === FIX_METHOD.ECONOMIC_METHOD;

// Przy naprawie metodą gospodarczą nie pytamy o faktury
export const isInvoicesAnswered = (val: Record<string, unknown>) =>
  !!val[ANY_INVOICES_NAME] || isEconomicFixMethod(val);

const hasInvoices = (val: Record<string, unknown>) =>
  val[ANY_INVOICES_NAME] === ANY_INVOICES.YES && !isEconomicFixMethod(val);

export const HHSchemaDeadEndsStepOne = {
  noIssueReportedToInsurer: {
    condition: (val) =>
      val[INSURER_DECISION_NAME] === INSURER_DECISION.NO_DECISION ||
      (val[INSURER_DECISION_NAME] === INSURER_DECISION.NEGATIVE &&
        val[WILL_APPEAL_NAME] === WILL_APPEAL.NO),
    returnToStep: 0,
  },
  settlement: {
    condition: (val) => val[SETTLEMENT_NAME] === SETTLEMENT.YES,
    returnToStep: 0,
  },
  fixCostDifference: {
    condition: (val) => {
      if (
        val[HOW_MUCH_INSURER_PAID_NAME] &&
        val[HOW_MUCH_REPAIR_COST_NAME] &&
        val[DAMAGE_REPAIRED_NAME] === DAMAGE_REPAIRED.YES
      ) {
        const howMuchInsurerPaid = Number(val[HOW_MUCH_INSURER_PAID_NAME]);
        const howMuchRepairCost = Number(val[HOW_MUCH_REPAIR_COST_NAME]);

        const diff = howMuchRepairCost - howMuchInsurerPaid;

        return diff < 1000;
      }

      return false;
    },
    returnToStep: 1,
  },
  fixCostEnough: {
    condition: (val) =>
      val[INSURER_DECISION_NAME] !== INSURER_DECISION.NEGATIVE &&
      val[SETTLEMENT_WAS_SUFFICIENT_NAME] === SETTLEMENT_WAS_SUFFICIENT.YES,
    returnToStep: 2,
  },
} as const satisfies DeadEnds;

export const HHSchemaDeadEndsStepOneNoIssueType = {
  noIssueReportedToInsurer: {
    condition: (val) =>
      val[INSURER_DECISION_NAME] === INSURER_DECISION.NO_DECISION ||
      (val[INSURER_DECISION_NAME] === INSURER_DECISION.NEGATIVE &&
        val[WILL_APPEAL_NAME] === WILL_APPEAL.NO),
    returnToStep: 1,
  },
  settlement: {
    condition: (val) => val[SETTLEMENT_NAME] === SETTLEMENT.YES,
    returnToStep: 1,
  },
  fixCostDifference: {
    condition: (val) => {
      if (
        val[HOW_MUCH_INSURER_PAID_NAME] &&
        val[HOW_MUCH_REPAIR_COST_NAME] &&
        val[DAMAGE_REPAIRED_NAME] === DAMAGE_REPAIRED.YES
      ) {
        const howMuchInsurerPaid = Number(val[HOW_MUCH_INSURER_PAID_NAME]);
        const howMuchRepairCost = Number(val[HOW_MUCH_REPAIR_COST_NAME]);

        const diff = howMuchRepairCost - howMuchInsurerPaid;

        return diff < 1000;
      }

      return false;
    },
    returnToStep: 2,
  },
  fixCostEnough: {
    condition: (val) =>
      val[INSURER_DECISION_NAME] !== INSURER_DECISION.NEGATIVE &&
      val[SETTLEMENT_WAS_SUFFICIENT_NAME] === SETTLEMENT_WAS_SUFFICIENT.YES,
    returnToStep: 3,
  },
} as const satisfies DeadEnds;

const stepOneProgressPoints = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: Record<any, any>,
  additionalInfo: AdditionalInfo
) => {
  const isFlooding = additionalInfo?.issueType === FLOODING;
  const isEmptyIssueType = isNil(additionalInfo?.issueType);

  const points = [
    ISSUE_TYPE_NAME,
    INSURER_DECISION_NAME,
    SETTLEMENT_NAME,
    HOW_MUCH_INSURER_PAID_NAME,
    DAMAGE_REPAIRED_NAME,
    HOW_MUCH_REPAIR_COST_NAME,
    SETTLEMENT_WAS_SUFFICIENT_NAME,
    FIX_METHOD_NAME,
    ANY_INVOICES_NAME,
    WHAT_WAS_REPAIRED_NAME,
    DRYING_METHOD_NAME,
    SETTLEMENT_FORM_NAME,
    POLICY_TYPE_NAME,
    IS_COVERED_BY_MORTGAGE_NAME,
    BANK_CESSION_NAME,
    BANK_NAME,
    PROPERTY_OWNERSHIP_STATUS_NAME,
    OWNERSHIP_TYPE_NAME,
    VAT_PAYER_NAME,
  ];

  const fieldsToRemove = [];

  if (!isEmptyIssueType) {
    fieldsToRemove.push(ISSUE_TYPE_NAME);
  }

  if (!isFlooding) {
    fieldsToRemove.push(
      WHAT_WAS_REPAIRED_NAME,
      DRYING_METHOD_NAME,
      SETTLEMENT_FORM_NAME
    );
  }

  if (isFlooding) {
    if (
      !isInvoicesAnswered(value) ||
      value[DAMAGE_REPAIRED_NAME] === DAMAGE_REPAIRED.NO ||
      (value[WHAT_WAS_REPAIRED_NAME]?.length &&
        !value[WHAT_WAS_REPAIRED_NAME]?.some(
          (item: EnumLike) => item.value === WHAT_WAS_REPAIRED_OPTIONS.DRYING
        ) &&
        value[DAMAGE_REPAIRED_NAME] !== DAMAGE_REPAIRED.NO)
    ) {
      fieldsToRemove.push(DRYING_METHOD_NAME, SETTLEMENT_FORM_NAME);
    }
    if (value[DRYING_METHOD_NAME] !== DRYING_METHOD.DRYING_COMPANY) {
      fieldsToRemove.push(SETTLEMENT_FORM_NAME);
    }
  }

  if (value[INSURER_DECISION_NAME] === INSURER_DECISION.NEGATIVE) {
    fieldsToRemove.push(SETTLEMENT_WAS_SUFFICIENT_NAME);
  }

  if (value[DAMAGE_REPAIRED_NAME] === DAMAGE_REPAIRED.NO) {
    fieldsToRemove.push(
      HOW_MUCH_REPAIR_COST_NAME,
      SETTLEMENT_WAS_SUFFICIENT_NAME,
      FIX_METHOD_NAME,
      ANY_INVOICES_NAME
    );
  }

  if (isEconomicFixMethod(value)) {
    fieldsToRemove.push(ANY_INVOICES_NAME);
  }

  if (value[OWNERSHIP_TYPE_NAME] !== OWNERSHIP_TYPE.COMPANY) {
    fieldsToRemove.push(VAT_PAYER_NAME);
  }

  if (
    !!value[POLICY_TYPE_NAME] &&
    ![POLICY_TYPE.OWN_POLICY, POLICY_TYPE.AGRICULTURAL_POLICY].includes(
      value[POLICY_TYPE_NAME]
    )
  ) {
    fieldsToRemove.push(
      IS_COVERED_BY_MORTGAGE_NAME,
      BANK_CESSION_NAME,
      BANK_NAME
    );
  }

  if (value[IS_COVERED_BY_MORTGAGE_NAME] === IS_COVERED_BY_MORTGAGE.NO) {
    fieldsToRemove.push(BANK_CESSION_NAME, BANK_NAME);
  }

  if (value[BANK_CESSION_NAME] === BANK_CESSION.NO) {
    fieldsToRemove.push(BANK_NAME);
  }

  const uniqueFieldsToRemove = [...new Set(fieldsToRemove)];

  return points.filter((field) => !uniqueFieldsToRemove.includes(field));
};

const stepTwoProgressPoints = () => {
  const points = [
    DAMAGE_LOCATION_ADDRESS,
    DAMAGE_LOCATION_ZIP_CODE,
    DAMAGE_LOCATION_CITY,
    DAMAGE_LOCATION_BUILDING_NUMBER,
  ];
  return points;
};

const documentsLastStep = (value: Record<string, unknown>) => {
  const withDryingReport =
    value[SETTLEMENT_FORM_NAME] === SETTLEMENT_FORM.ASSIGNMENT;

  if (
    value[POLICY_TYPE_NAME] === POLICY_TYPE.OWN_POLICY ||
    value[POLICY_TYPE_NAME] === POLICY_TYPE.AGRICULTURAL_POLICY
  ) {
    if (hasInvoices(value)) {
      return {
        lastStep: withDryingReport ? 7 : 6,
        additionalStep: true,
        secondAdditionalStep: true,
        dryingReport: withDryingReport,
      };
    }
    return {
      lastStep: withDryingReport ? 6 : 5,
      additionalStep: true,
      secondAdditionalStep: false,
      dryingReport: withDryingReport,
    };
  }

  if (hasInvoices(value)) {
    return {
      lastStep: withDryingReport ? 6 : 5,
      additionalStep: false,
      secondAdditionalStep: true,
      dryingReport: withDryingReport,
    };
  }

  return {
    lastStep: withDryingReport ? 5 : 4,
    additionalStep: false,
    secondAdditionalStep: false,
    dryingReport: withDryingReport,
  };
};

const subSteps = (value: Record<string, unknown>) => {
  const withDryingReport =
    value[SETTLEMENT_FORM_NAME] === SETTLEMENT_FORM.ASSIGNMENT;

  if (
    value[POLICY_TYPE_NAME] === POLICY_TYPE.OWN_POLICY ||
    value[POLICY_TYPE_NAME] === POLICY_TYPE.AGRICULTURAL_POLICY
  ) {
    if (hasInvoices(value)) {
      return withDryingReport
        ? hhDocumentsStepsWithBothAndDryingReport
        : hhDocumentsStepsWithBoth;
    }
    return withDryingReport
      ? hhDocumentsStepsWithPolicyAndDryingReport
      : hhDocumentsStepsWithPolicy;
  }

  if (hasInvoices(value)) {
    return withDryingReport
      ? hhDocumentsStepsWithInvoicesAndDryingReport
      : hhDocumentsStepsWithInvoices;
  }

  return withDryingReport ? hhDocumentsStepsWithDryingReport : hhDocumentsSteps;
};

export const HHSchema = [
  {
    label: i18n.t('Weryfikacja szkody'),
    component: Step1HH,
    chatMessage: () => {
      return (
        <>
          {i18n.t(
            'Pomogę Ci przejść przez etap weryfikacji szkody na Twojej nieruchomości. Udziel odpowiedzi na kilka podstawowych pytań i przyspiesz proces analizy.'
          )}
        </>
      );
    },
    hideChatMessageCloseButton: true,
    progressPoints: stepOneProgressPoints,
    deadEnds: (additionalInfo) => {
      if (isNil(additionalInfo.issueType)) {
        return HHSchemaDeadEndsStepOneNoIssueType;
      }

      return HHSchemaDeadEndsStepOne;
    },
    lastStep: (_, additionalInfo) => {
      if (isNil(additionalInfo?.issueType)) {
        return {
          lastStep: 4,
          additionalStep: false,
        };
      }

      return {
        lastStep: 3,
        additionalStep: false,
      };
    },
  },
  {
    label: i18n.t('Adres szkody'),
    component: Step2HH,
    progressPoints: stepTwoProgressPoints,
    backButton: () => {
      return false;
    },
    lastStep: () => {
      return {
        lastStep: 0,
        additionalStep: false,
      };
    },
    chatMessage: () => {
      return i18n.t(
        'Na podstawie adresu zweryfikuję koszty oraz upewnię się, że dokumenty pasują do nieruchomości. Dzięki temu przejdziemy przez proces sprawnie i unikniemy opóźnień.'
      );
    },
  },
  {
    label: i18n.t('Dokumenty'),
    component: Step3HH,
    subSteps,
    chatMessage: () => {
      return i18n.t('Dodaj dokumenty i przyspiesz analizę Twojej sprawy.');
    },
    backButton: (value) => {
      if (value === 0) {
        return false;
      }
      return true;
    },
    lastStep: documentsLastStep,
  },
  {
    label: i18n.t('Zakończ'),
    lastStep: () => {
      return {
        lastStep: 0,
        additionalStep: false,
      };
    },
    name: '',
  },
] as ExtendedSteps[];
